import { motion } from "framer-motion";
import { Briefcase, Heart, ShieldCheck, TrendingUp, Users } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { openChat } from "../../lib/api";
import { useContent, pick } from "../../lib/content";

const Frame = ({ src, alt, testid }) => (
  <div className="group relative overflow-hidden rounded-[2rem] border border-amber-800/40" data-testid={testid}>
    <motion.img src={src} alt={alt} initial={{ scale: 1.15 }} whileInView={{ scale: 1 }}
      viewport={{ once: true, amount: 0.1 }} transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
      className="aspect-[4/5] h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,transparent_20%,rgba(10,9,8,0.8)_85%)]" />
  </div>
);

export const Love = () => {
  const c = useContent();
  return (
  <section id="amour" className="relative mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-36" data-testid="love-section">
    <div className="grid items-center gap-12 lg:grid-cols-12">
      <Reveal className="lg:col-span-5">
        <Frame src={pick(c, "img_love", "/img/candles4.jpg")} alt="Bougies d'attraction" testid="love-image" />
      </Reveal>
      <div className="lg:col-span-6 lg:col-start-7">
        <Reveal><Eyebrow>01 — Retour d'affection</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 font-display text-3xl leading-tight text-amber-50 sm:text-4xl lg:text-5xl">
            Faites Revenir l'Être Aimé Définitivement
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-8 inline-flex items-baseline gap-3 rounded-2xl border border-dashed border-amber-400/60 bg-amber-400/10 px-6 py-4" data-testid="love-price">
            <span className="font-mono-x text-xs uppercase tracking-[0.2em] text-amber-300">Tarif unique</span>
            <span className="font-display text-3xl text-amber-200">150€</span>
            <span className="text-sm text-stone-300">après satisfaction totale</span>
          </div>
        </Reveal>
        <Reveal delay={0.3}>
          <p className="mt-8 text-base leading-relaxed text-stone-300 md:text-lg whitespace-pre-line">
            {pick(c, "love_paragraph",
              "Que ce soit pour faire revenir un homme ou une femme qui vous a quitté, les rituels de haute magie blanche et magie noire du Maître Wouffa agissent immédiatement. Fournissez simplement son nom dans la discussion instantanée, le rituel s'active et la cible est totalement maîtrisée : elle ne repartira plus jamais.")}
          </p>
        </Reveal>
        <Reveal delay={0.4}>
          <button onClick={() => openChat("Bonjour Maître, je souhaite faire revenir l'être aimé. Son prénom est : ")}
            data-testid="love-chat-button"
            className="mt-10 flex items-center gap-3 rounded-full border border-amber-500/60 px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] text-amber-200 transition-colors duration-300 hover:bg-amber-400 hover:text-stone-950">
            <Heart className="h-4 w-4" /> Engager le rituel amoureux
          </button>
        </Reveal>
      </div>
    </div>
  </section>
  );
};

const perks = [
  [TrendingUp, "Vous obtiendrez des", "promotions", "rapidement."],
  [ShieldCheck, "Vos supérieurs", "ne pourront plus jamais vous renvoyer", "."],
  [Users, "Au travail,", "ils vous aimeront tous", "et respecteront votre parole."],
];

export const Work = () => {
  const c = useContent();
  return (
  <section id="travail" className="relative overflow-hidden bg-[#12100e] py-24 md:py-36" data-testid="work-section">
    <div className="pointer-events-none absolute -right-40 top-10 h-[500px] w-[500px] rounded-full bg-[#8b0000]/20 blur-[120px]" />
    <div className="relative mx-auto grid max-w-7xl gap-12 px-5 md:px-10 lg:grid-cols-12">
      <div className="lg:col-span-6">
        <Reveal><Eyebrow>02 — Domaine professionnel</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 font-display text-3xl leading-tight text-amber-50 sm:text-4xl lg:text-5xl">
            Domination &amp; Promotion
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 font-serif-it text-2xl italic text-amber-200">Rituel spécial pour le travail.</p>
          <p className="mt-4 text-base leading-relaxed text-stone-300 md:text-lg whitespace-pre-line">
            {pick(c, "work_paragraph",
              "Si vous donnez les noms de vos patrons dans le chat, le Maître Wouffa débloque votre carrière en toute discrétion.")}
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <button onClick={() => openChat("Bonjour Maître, voici les noms de mes patrons : ")} data-testid="work-chat-button"
            className="mt-10 flex items-center gap-3 rounded-full bg-amber-400 px-7 py-3.5 text-sm font-bold uppercase tracking-[0.15em] text-stone-950 transition-transform duration-300 hover:scale-[1.03] active:scale-95">
            <Briefcase className="h-4 w-4" /> Indiquer le nom de mes supérieurs
          </button>
        </Reveal>
      </div>
      <div className="flex flex-col gap-4 lg:col-span-6">
        {perks.map(([Icon, a, b, c], i) => (
          <Reveal key={b} delay={0.1 * i}>
            <motion.div whileHover={{ x: 8 }} transition={{ type: "spring", stiffness: 200, damping: 20 }}
              className="flex items-start gap-5 rounded-2xl border border-amber-900/40 bg-[#181512] p-6 md:p-8" data-testid={`work-perk-${i}`}>
              <span className="font-display text-3xl text-amber-500/60">0{i + 1}</span>
              <div className="flex-1">
                <Icon className="mb-3 h-5 w-5 text-amber-400" />
                <p className="text-lg text-stone-200">{a} <strong className="text-amber-100">{b}</strong> {c}</p>
              </div>
            </motion.div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};
